/** Notification template editor — Phase 2E-tmpl.
 *
 * Six templates per org: (invite, reminder_24h, reminder_1h) ×
 * (email, sms). The editor lets the admin override subject + body
 * per (kind, channel); unmodified slots fall back to baked-in defaults
 * server-side.
 *
 * "Available variables" hint is sourced from the defaults endpoint
 * (which also gives us the default text for the "Reset to default"
 * affordance). Variables get rendered server-side at send time; the
 * frontend doesn't preview interpolated content — admins see the raw
 * template with `{join_url}`-style placeholders. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { TemplatePreviewAndTest } from "@/components/TemplatePreviewAndTest";
import { ApiPaths, api } from "@/lib/api";


type Kind =
  | "invite"
  | "reminder_24h"
  | "reminder_1h"
  | "confirmation"
  | "rescheduled"
  | "booking_invite";
type Channel = "email" | "sms";

type Defaults = {
  variables: Record<string, string>;
  defaults: { kind: Kind; channel: Channel; subject: string; body_text: string }[];
};

type CustomRow = {
  id: string;
  kind: Kind;
  channel: Channel;
  subject: string;
  body_text: string;
  enabled: boolean;
  updated_at: string;
};


const KIND_KEYS: Record<Kind, string> = {
  invite: "admin.notifications.kindInvite",
  reminder_24h: "admin.notifications.kindReminder24h",
  reminder_1h: "admin.notifications.kindReminder1h",
  confirmation: "admin.notifications.kindConfirmation",
  rescheduled: "admin.notifications.kindRescheduled",
  booking_invite: "admin.notifications.kindBookingInvite",
};

const CHANNEL_KEYS: Record<Channel, string> = {
  email: "admin.notifications.channelEmail",
  sms: "admin.notifications.channelSms",
};


export function NotificationTemplatesCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [kind, setKind] = useState<Kind>("invite");
  const [channel, setChannel] = useState<Channel>("email");

  // Preview the baked-in defaults in the language messages actually send
  // in — the send-time fallback is French (no per-org locale; recipient
  // Session.locale / Contact.language drive real sends, blank → French).
  // Previewing in the admin's UI language (e.g. English) was misleading:
  // a French org's beneficiaries never receive those English defaults.
  const previewLocale = "fr";
  const defaultsQ = useQuery({
    queryKey: ["notification-template-defaults", previewLocale],
    queryFn: async () =>
      (
        await api.get<Defaults>(ApiPaths.notificationTemplateDefaults, {
          params: { locale: previewLocale },
        })
      ).data,
  });
  const customQ = useQuery({
    queryKey: ["notification-templates"],
    queryFn: async () => (await api.get<CustomRow[]>(ApiPaths.notificationTemplates)).data,
  });

  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [touched, setTouched] = useState(false);

  // Defensive normalisation: a malformed (non-array) response must not
  // crash the card into a remount loop — `.find`/`.some` below would
  // throw on a non-array. Memoised so the refs stay stable for the
  // effect deps.
  const customRows = useMemo(
    () => (Array.isArray(customQ.data) ? customQ.data : []),
    [customQ.data],
  );
  const defaultRows = useMemo(
    () => (Array.isArray(defaultsQ.data?.defaults) ? defaultsQ.data.defaults : []),
    [defaultsQ.data],
  );

  // Whenever the selected slot OR the underlying data changes, refresh
  // the local edit state from the persisted row (or default).
  useEffect(() => {
    if (!defaultsQ.data) return;
    const custom = customRows.find(
      (r) => r.kind === kind && r.channel === channel,
    );
    if (custom) {
      setSubject(custom.subject);
      setBodyText(custom.body_text);
    } else {
      const def = defaultRows.find(
        (d) => d.kind === kind && d.channel === channel,
      );
      setSubject(def?.subject ?? "");
      setBodyText(def?.body_text ?? "");
    }
    setTouched(false);
  }, [kind, channel, defaultsQ.data, customRows, defaultRows]);

  const save = useMutation({
    mutationFn: async () => {
      const res = await api.put<CustomRow>(
        ApiPaths.notificationTemplate(kind, channel),
        { subject, body_text: bodyText },
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notification-templates"] });
      setTouched(false);
    },
  });

  const reset = useMutation({
    mutationFn: async () => {
      await api.delete(ApiPaths.notificationTemplate(kind, channel));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notification-templates"] });
    },
  });

  if (defaultsQ.isLoading || customQ.isLoading) {
    return (
      <section className="card text-sm text-nb-text-2">
        {t("admin.notifications.loading")}
      </section>
    );
  }
  if (!defaultsQ.data) return null;

  const variables = defaultsQ.data.variables;
  const isCustomised = customRows.some(
    (r) => r.kind === kind && r.channel === channel,
  );

  return (
    <section className="card space-y-3">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("admin.notifications.cardTitle")}
        </h2>
        <p className="text-xs text-nb-text-3">
          <Trans
            i18nKey="admin.notifications.cardHint"
            values={{ varExample: "{join_url}" }}
            components={[<code key="code" />]}
          />
        </p>
      </header>

      <div className="flex flex-wrap gap-3">
        <label className="text-sm">
          <span className="block text-nb-text-2">{t("admin.notifications.kindLabel")}</span>
          <select
            className="input"
            value={kind}
            onChange={(e) => setKind(e.target.value as Kind)}
            aria-label={t("admin.notifications.kindAriaLabel")}
          >
            {(Object.keys(KIND_KEYS) as Kind[]).map((k) => (
              <option key={k} value={k}>
                {t(KIND_KEYS[k])}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-nb-text-2">{t("admin.notifications.channelLabel")}</span>
          <select
            className="input"
            value={channel}
            onChange={(e) => setChannel(e.target.value as Channel)}
            aria-label={t("admin.notifications.channelAriaLabel")}
          >
            {(Object.keys(CHANNEL_KEYS) as Channel[]).map((c) => (
              <option key={c} value={c}>
                {t(CHANNEL_KEYS[c])}
              </option>
            ))}
          </select>
        </label>
        <span className="self-end text-xs">
          {isCustomised ? (
            <span className="text-nb-mint">{t("admin.notifications.customised")}</span>
          ) : (
            <span className="text-nb-text-3">{t("admin.notifications.usingDefault")}</span>
          )}
        </span>
      </div>

      {channel === "email" && (
        <label className="block space-y-1 text-sm">
          <span className="text-nb-text-2">{t("admin.notifications.subject")}</span>
          <input
            className="input w-full"
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              setTouched(true);
            }}
            aria-label={t("admin.notifications.subjectAriaLabel")}
          />
        </label>
      )}

      <label className="block space-y-1 text-sm">
        <span className="text-nb-text-2">
          {channel === "email"
            ? t("admin.notifications.body")
            : t("admin.notifications.message")}
        </span>
        <textarea
          className="input w-full font-mono text-xs"
          rows={channel === "sms" ? 3 : 10}
          value={bodyText}
          onChange={(e) => {
            setBodyText(e.target.value);
            setTouched(true);
          }}
          aria-label={t("admin.notifications.bodyAriaLabel")}
        />
      </label>

      <details className="text-xs">
        <summary className="cursor-pointer text-nb-text-2 hover:text-nb-text">
          {t("admin.notifications.availableVariables", {
            count: Object.keys(variables).length,
          })}
        </summary>
        <ul className="mt-2 space-y-1">
          {Object.entries(variables).map(([name, desc]) => (
            <li key={name}>
              <code className="text-nb-mint">{`{${name}}`}</code>
              <span className="ml-2 text-nb-text-3">{desc}</span>
            </li>
          ))}
        </ul>
      </details>

      <TemplatePreviewAndTest
        kind={kind}
        channel={channel}
        subject={subject}
        bodyText={bodyText}
      />

      {save.isError && (
        <p className="text-xs text-cvp-danger">
          {(save.error as Error).message ?? t("admin.notifications.saveErrorGeneric")}
        </p>
      )}

      <div className="flex justify-end gap-2">
        {isCustomised && (
          <button
            type="button"
            className="btn-ghost text-sm text-cvp-danger hover:bg-cvp-danger/10"
            onClick={() => {
              if (confirm(t("admin.notifications.resetConfirm"))) {
                reset.mutate();
              }
            }}
            disabled={reset.isPending}
          >
            {t("admin.notifications.resetButton")}
          </button>
        )}
        <button
          type="button"
          className="btn-primary text-sm"
          onClick={() => save.mutate()}
          disabled={!touched || save.isPending}
        >
          {save.isPending
            ? t("admin.notifications.saving")
            : t("admin.notifications.saveButton")}
        </button>
      </div>
    </section>
  );
}
